/**
 * 监控列表「当前生效客户」列（只读，权限门控）。
 *
 * 视图覆盖：紧凑视图与默认的列表视图都引本文件，保证授权用户在默认界面就能看到，
 * 不需要手动切视图（监控默认是列表视图，且视图选择不持久化）。
 *
 * 权限：sales:flight-attribution:customer-view —— 与归属页的 :read 分开授权，
 * 运控只看客户名，不顺带获得改客户入口。**该权限默认不进任何角色组**，
 * 由管理员逐人授予；无权限返回空数组（沿用 transactionColumns 的列级门控写法），
 * 而不是渲染空列——否则会被读成「这班没客户」。
 *
 * 格子内容：当前生效客户名；这班绑了合同时，名字后用括号附上合同性质。合同由后端按
 * 「与客户同一来源」解析：订单客户配订单上绑的合同，归属客户配标记上钉的意向合同；
 * 没绑合同（或性质认不出）时只有客户名。三种合同性质的中文与销售合同页同一套，
 * 直接引用销售应收管理页的常量，不在监控另起名字。外组织确认的标记客户名后端已按
 * 数据范围过滤，前端不做二次判断。
 *
 * 列宽按「原先给客户名的空间 + 最长性质括号」加出来，列表和紧凑各自传入。
 * 单元格从右裁切会先切掉性质，所以不用表格自带省略；名字按格子宽度收缩，性质整词保留。
 */

import React from "react";
import { Tooltip } from "antd";

import { CONTRACT_KIND_TEXT } from "../../../sales/RevenueManagement/salesRevenueConstants";

export const CUSTOMER_VIEW_PERMISSION = "sales:flight-attribution:customer-view";

/** 当前用户是否有权看客户列（超管按通配规则可见）。 */
export const canViewCustomerColumn = (user) =>
    Boolean(user && (user.is_superuser || user.permissions?.includes(CUSTOMER_VIEW_PERMISSION)));

/**
 * 客户列定义。
 *
 * @param {Object} options
 * @param {Object} options.user - 当前用户
 * @param {number} [options.width] - 列宽，两个视图的表格密度不同，由调用方给
 * @returns {Array} 无权限时返回空数组
 */
export const getCustomerColumn = ({ user, width = 156 }) => {
    if (!canViewCustomerColumn(user)) return [];

    return [
        {
            title: <Tooltip title="当前生效客户；有绑定时附带合同性质">客户</Tooltip>,
            dataIndex: "customer_name",
            key: "customer_name",
            width,
            className: "text-left",
            render: (text, record) => {
                if (!text) return "-";
                const kindLabel = record.contract_kind === "ACMI" ? "时段包机" : CONTRACT_KIND_TEXT[record.contract_kind];
                const fullLabel = kindLabel ? `${text}（${kindLabel}）` : text;
                return (
                    <Tooltip title={fullLabel}>
                        <span
                            style={{
                                display: "flex",
                                flex: "1 1 auto",
                                width: "100%",
                                minWidth: 0,
                                overflow: "hidden",
                                alignItems: "baseline",
                                fontSize: "10px",
                            }}
                        >
                            <span style={{ flex: "0 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{text}</span>
                            {kindLabel ? <span style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}>{`（${kindLabel}）`}</span> : null}
                        </span>
                    </Tooltip>
                );
            },
        },
    ];
};
